import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import ReportProblemOutlinedIcon from '@mui/icons-material/ReportProblemOutlined';
import ThumbUpAltOutlinedIcon from '@mui/icons-material/ThumbUpAltOutlined';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import SendIcon from '@mui/icons-material/Send';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { submitContact } from '../api';

const TYPE_CONFIG = {
  reclamation: { icon: <ReportProblemOutlinedIcon fontSize="small" />, color: '#4fc3f7' },
  avis:        { icon: <ThumbUpAltOutlinedIcon fontSize="small" />,    color: '#4fc3f7' },
  demande:     { icon: <HelpOutlineIcon fontSize="small" />,           color: '#4fc3f7' },
};

const inputSx = {
  '& .MuiOutlinedInput-root': {
    bgcolor: 'rgba(255,255,255,0.04)',
    '& fieldset': { borderColor: 'rgba(255,255,255,0.15)' },
    '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.35)' },
  },
  '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.5)' },
  '& .MuiInputBase-input': { color: '#fff' },
};

const INITIAL = { type: 'reclamation', name: '', email: '', subject: '', message: '' };

export default function ContactForm() {
  const { t } = useTranslation();
  const [form, setForm] = useState(INITIAL);
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState('idle'); // idle | loading | success | error
  const [serverError, setServerError] = useState('');

  function set(field) {
    return (e) => {
      setForm((f) => ({ ...f, [field]: e.target.value }));
      setErrors((er) => ({ ...er, [field]: undefined }));
    };
  }

  function validate() {
    const e = {};
    if (!form.name.trim())    e.name    = t('contact.errors.nameRequired');
    if (!form.email.trim())   e.email   = t('contact.errors.emailRequired');
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email))
                               e.email   = t('contact.errors.emailInvalid');
    if (!form.subject.trim()) e.subject = t('contact.errors.subjectRequired');
    if (!form.message.trim()) e.message = t('contact.errors.messageRequired');
    return e;
  }

  async function handleSubmit(e) {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); return; }
    setStatus('loading');
    setServerError('');
    try {
      await submitContact(form);
      setStatus('success');
      setForm(INITIAL);
    } catch (err) {
      setServerError(err.message || t('contact.errors.generic'));
      setStatus('error');
    }
  }

  if (status === 'success') {
    return (
      <Stack alignItems="center" spacing={2} sx={{ py: 6, px: 3, textAlign: 'center' }}>
        <CheckCircleIcon sx={{ fontSize: 64, color: '#66bb6a' }} />
        <Typography variant="h6" fontWeight={700} color="#fff">
          {t('contact.successTitle')}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t('contact.successDesc')}
        </Typography>
        <Button variant="outlined" sx={{ mt: 2 }} onClick={() => setStatus('idle')}>
          {t('contact.newMessage')}
        </Button>
      </Stack>
    );
  }

  const activeColor = TYPE_CONFIG[form.type]?.color || '#4fc3f7';

  return (
    <Box component="form" onSubmit={handleSubmit} noValidate>
      <Stack spacing={3}>

        {/* Type selector */}
        <Box>
          <Typography variant="caption" color="rgba(255,255,255,0.5)" sx={{ mb: 1, display: 'block', textTransform: 'uppercase', letterSpacing: 1 }}>
            {t('contact.typeLabel')}
          </Typography>
          <ToggleButtonGroup
            exclusive
            fullWidth
            value={form.type}
            onChange={(_, v) => v && setForm((f) => ({ ...f, type: v }))}
            sx={{
              gap: 1,
              '& .MuiToggleButtonGroup-grouped': { border: '1px solid rgba(255,255,255,0.15) !important', borderRadius: '10px !important' },
            }}
          >
            {Object.entries(TYPE_CONFIG).map(([key, { icon, color }]) => (
              <ToggleButton
                key={key}
                value={key}
                sx={{
                  flex: 1,
                  py: 1.2,
                  gap: 0.8,
                  color: 'rgba(255,255,255,0.5)',
                  bgcolor: 'rgba(255,255,255,0.04)',
                  textTransform: 'capitalize',
                  fontWeight: 600,
                  fontSize: '0.78rem',
                  '&.Mui-selected': {
                    color: '#fff',
                    bgcolor: `${color}22`,
                    borderColor: `${color} !important`,
                    boxShadow: `0 0 0 1px ${color}`,
                  },
                  '&.Mui-selected svg': { color },
                }}
              >
                {icon}
                {t(`contact.types.${key}`)}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Box>

        {/* Name + Email */}
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <TextField
            label={t('contact.name')}
            value={form.name}
            onChange={set('name')}
            error={!!errors.name}
            helperText={errors.name}
            fullWidth
            sx={inputSx}
          />
          <TextField
            label={t('contact.email')}
            type="email"
            value={form.email}
            onChange={set('email')}
            error={!!errors.email}
            helperText={errors.email}
            fullWidth
            sx={inputSx}
          />
        </Stack>

        {/* Subject */}
        <TextField
          label={t('contact.subject')}
          value={form.subject}
          onChange={set('subject')}
          error={!!errors.subject}
          helperText={errors.subject}
          fullWidth
          sx={inputSx}
        />

        {/* Message */}
        <TextField
          label={t('contact.message')}
          value={form.message}
          onChange={set('message')}
          error={!!errors.message}
          helperText={errors.message}
          multiline
          minRows={4}
          fullWidth
          sx={inputSx}
        />

        {status === 'error' && (
          <Alert severity="error">
            <AlertTitle>{t('contact.errors.submitFailed')}</AlertTitle>
            {serverError}
          </Alert>
        )}

        <Button
          type="submit"
          variant="contained"
          size="large"
          endIcon={status === 'loading' ? <CircularProgress size={18} color="inherit" /> : <SendIcon />}
          disabled={status === 'loading'}
          sx={{
            bgcolor: activeColor,
            color: '#0a1628',
            fontWeight: 700,
            py: 1.4,
            fontSize: '0.95rem',
            '&:hover': { bgcolor: activeColor, filter: 'brightness(1.12)' },
            '&.Mui-disabled': { bgcolor: 'rgba(255,255,255,0.12)', color: 'rgba(255,255,255,0.3)' },
          }}
        >
          {status === 'loading' ? t('contact.sending') : t('contact.submit')}
        </Button>

      </Stack>
    </Box>
  );
}
